/**
 * Save-time helpers for the EventModal.
 *
 * Extracted so the component stays under the file-length standard and the
 * alias-stamping and ERTS-history construction can be reasoned about (and
 * tested) independently of React state.
 */
import { UnscheduledEvent } from '../../types';
import {
  EVENT_STATUS,
  ERTS_CONFIDENCE,
  ERTS_INITIAL_CHANGE_REASON,
  ERTS_FINAL_CHANGE_REASON,
} from '../../constants';
import { getErtsTimeDisplay } from './dateTimeUtils';

/** A single ERTS history record shown in the ERTS history panel. */
export interface ErtsHistoryEntry {
  ertsDate: string;
  ertsTime: string;
  confidence: string;
  changeReason: string;
  submittedBy: string;
  submittedAt: string;
}

/**
 * Derives the user alias (local part of the email) or '' when no user.
 */
export function aliasFromEmail(email: string | undefined): string {
  return email ? email.split('@')[0] : '';
}

/**
 * Stamps audit aliases and the completion timestamp on a record. Created By and
 * Completed By are write-once; Last Edited By updates on every save. When the
 * event is Completed, `completionTimeZulu` is set (write-once) to the event's
 * return-to-service time — matching synced rows and, crucially, the field the
 * History/Completed views and the server-side history window filter on. Without
 * it, a live-completed event would be excluded from those views. Mutates and
 * returns the record.
 */
export function applyAuditAliases(
  record: UnscheduledEvent,
  email: string | undefined,
): UnscheduledEvent {
  const alias = aliasFromEmail(email);
  if (!record.createdByAlias) record.createdByAlias = alias;
  record.lastEditedByAlias = alias;
  if (record.status === EVENT_STATUS.COMPLETED && !record.completedByAlias) {
    record.completedByAlias = alias;
  }
  if (record.status === EVENT_STATUS.COMPLETED && !record.completionTimeZulu) {
    record.completionTimeZulu = record.returnToServiceTimeZulu || nowZuluTimestamp();
  }
  return record;
}

/**
 * Formats the current time as a Zulu `YYYY-MM-DD HH:MM` submission timestamp.
 */
export function nowZuluTimestamp(now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const mo = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  const h = String(now.getUTCHours()).padStart(2, '0');
  const mi = String(now.getUTCMinutes()).padStart(2, '0');
  return `${y}-${mo}-${d} ${h}:${mi}`;
}

/** Inputs needed to build an ERTS history entry from the current edit. */
export interface ErtsEntryInput {
  record: UnscheduledEvent;
  confidence: string;
  changeReason: string;
  changeReasonOther: string;
  email: string | undefined;
}

/**
 * Builds a new ERTS history entry from the in-progress ERTS edit. Pure: the
 * caller persists the resulting reason/confidence on the record.
 */
export function buildErtsHistoryEntry(input: ErtsEntryInput): ErtsHistoryEntry {
  const { record, confidence, changeReason, changeReasonOther, email } = input;
  const reason = changeReason === 'Other' ? changeReasonOther : changeReason;
  return {
    ertsDate: record.returnToServiceTimeZulu ? record.returnToServiceTimeZulu.split(' ')[0] : '',
    ertsTime: getErtsTimeDisplay(record.returnToServiceTimeZulu),
    confidence,
    changeReason: reason,
    submittedBy: aliasFromEmail(email),
    submittedAt: nowZuluTimestamp(),
  };
}

/** Inputs needed to build the ERTS history entries committed on save. */
export interface ErtsCommitInput {
  record: UnscheduledEvent;
  confidence: string;
  changeReason: string;
  changeReasonOther: string;
  email: string | undefined;
  existingHistory: ErtsHistoryEntry[];
}

/**
 * Builds the ERTS history entries to append for the current commit.
 *
 * Normally this is a single entry for the in-progress edit. When an event is
 * completed with a Final ERTS and no Initial ERTS was ever logged, an Initial
 * ERTS entry (High confidence) is synthesized from the same ERTS date/time so
 * the history is complete. The entries are returned oldest-first (Initial then
 * Final); display ordering is handled by {@link sortErtsHistoryForDisplay}.
 */
export function buildErtsCommitEntries(input: ErtsCommitInput): ErtsHistoryEntry[] {
  const { record, confidence, changeReason, changeReasonOther, email, existingHistory } = input;
  const finalEntry = buildErtsHistoryEntry({
    record,
    confidence,
    changeReason,
    changeReasonOther,
    email,
  });
  const isCompletionFinal =
    record.status === EVENT_STATUS.COMPLETED && changeReason === ERTS_FINAL_CHANGE_REASON;
  const hasInitial = existingHistory.some(
    (entry) => entry.changeReason === ERTS_INITIAL_CHANGE_REASON,
  );
  if (isCompletionFinal && !hasInitial) {
    const initialEntry = buildErtsHistoryEntry({
      record,
      confidence: ERTS_CONFIDENCE.HIGH,
      changeReason: ERTS_INITIAL_CHANGE_REASON,
      changeReasonOther: '',
      email,
    });
    return [initialEntry, finalEntry];
  }
  return [finalEntry];
}

/**
 * Sorts ERTS history newest-first for display. When two entries share a
 * submission timestamp (e.g. the Initial + Final pair logged together on
 * completion), the Initial ERTS is ordered last so the Final ERTS appears
 * first.
 */
export function sortErtsHistoryForDisplay(history: ErtsHistoryEntry[]): ErtsHistoryEntry[] {
  const initialRank = (reason: string): number => (reason === ERTS_INITIAL_CHANGE_REASON ? 1 : 0);
  return [...history].sort((a, b) => {
    const byTime = b.submittedAt.localeCompare(a.submittedAt);
    if (byTime !== 0) return byTime;
    return initialRank(a.changeReason) - initialRank(b.changeReason);
  });
}

/**
 * Normalizes the damage-related fields on a record at save time.
 *
 * Persists the chosen damage code (clearing it when Damage is unchecked), and
 * the Chargeable flag — which only applies to a damaged event combined with
 * Delay or CXL and a selected damage code. When it does not apply, Chargeable
 * is forced to false (the Non-Chargeable default) so stale values never persist.
 */
export function normalizeDamageFields(record: UnscheduledEvent, damageCode: string): void {
  record.damageCode = record.damage ? damageCode : '';
  const chargeableApplies =
    !!record.damage && (!!record.delay || !!record.cancellation) && !!damageCode;
  record.chargeable = chargeableApplies ? !!record.chargeable : false;
}
